import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams, IRowNode } from '@ag-grid-community/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import { AgCellWrapperComponent } from '../ag-cell-wrapper/ag-cell-wrapper.component';

export interface AgActionsComponentParams extends ICellRendererParams {
  deleteButtonDisabled$?: BehaviorSubject<boolean>;
  deleteClickFN?: (params: { rowNode: IRowNode }) => void;
  deletedRows?: BehaviorSubject<string[]>;
  disabled$?: Observable<boolean>;
  downloadButtonClass?: string;
  downloadClickFN?: (params: { rowNode: IRowNode }) => void;
  editClickFN?: (params: { rowNode: IRowNode }) => void;
  hideDeleteButton?: boolean;
  hideEditButton?: boolean;
  processing$?: Observable<boolean>;
  tooltipText$?: Observable<string>;
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent, TooltipDirective, AsyncPipe],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    @if (
      {
        disabled: params.disabled$ | async,
        processing: params.processing$ | async,
        deleteButtonDisabled: params.deleteButtonDisabled$ | async,
      };
      as obj
    ) {
      <div class="flex h-full items-center justify-center" [attr.auto-qa]="autoTestAttribute">
        @if (params.downloadClickFN) {
          <span [auxTooltip]="(params.tooltipText$ | async) || ''">
            <button
              class="flex h-8 w-8 items-center justify-center p-0"
              type="button"
              [disabled]="obj.disabled || obj.processing"
              [ngClass]="{ 'cursor-not-allowed opacity-50': obj.disabled || obj.processing }"
              (click)="onDownloadClick()"
            >
              <aux-icon name="Download" [ngClass]="params.downloadButtonClass || 'text-aux-blue'" />
            </button>
          </span>
        }
        <span [auxTooltip]="(params.tooltipText$ | async) || ''">
          @if (!params.hideEditButton && params.editClickFN) {
            <button
              class="flex h-8 w-8 items-center justify-center p-0"
              type="button"
              [disabled]="obj.disabled || obj.processing"
              [ngClass]="{ 'cursor-not-allowed opacity-50': obj.disabled || obj.processing }"
              (click)="onEditClick()"
            >
              <aux-icon name="Pencil" class="text-aux-blue" />
            </button>
          }
        </span>
        @if ({ trashIconVisible: isTrashIconVisible() }; as icon) {
          <span [auxTooltip]="(params.tooltipText$ | async) || ''">
            @if (!params.hideDeleteButton && params.deleteClickFN) {
              <button
                class="flex h-8 w-8 items-center justify-center p-0"
                type="button"
                [disabled]="obj.disabled || obj.processing || obj.deleteButtonDisabled"
                [ngClass]="{
                  'cursor-not-allowed opacity-50':
                    obj.disabled || obj.processing || obj.deleteButtonDisabled,
                }"
                (click)="onDeleteClick()"
              >
                @if (icon.trashIconVisible) {
                  <aux-icon name="Trash" class="text-aux-error" />
                }
                @if (!icon.trashIconVisible) {
                  <aux-icon name="TrashOff" />
                }
              </button>
            }
          </span>
        }
      </div>
    }
  `,
})
export class AgActionsComponent extends AgCellWrapperComponent implements ICellRendererAngularComp {
  params!: AgActionsComponentParams;

  agInit(params: AgActionsComponentParams) {
    this.params = params;

    if (this.params?.data?.sub_entity_type === 'Budget') {
      this.params.disabled$ = of(true);
    }

    this.autoTestAttribute = this.getAutoTestAttribute(params);
  }

  isTrashIconVisible(): boolean {
    const deletedRows = this.params.deletedRows?.getValue();

    if (!deletedRows?.length || !this.params?.data?.id) {
      return true;
    }

    return !deletedRows.includes(this.params.data.id);
  }

  onDeleteClick() {
    if (this.params.deleteClickFN) {
      this.params.deleteClickFN({ rowNode: this.params.node });
    }
  }

  onDownloadClick() {
    if (this.params.downloadClickFN) {
      this.params.downloadClickFN({ rowNode: this.params.node });
    }
  }

  onEditClick() {
    if (this.params.editClickFN) {
      this.params.editClickFN({ rowNode: this.params.node });
    }
  }

  refresh(): boolean {
    return false;
  }
}
